import debounce from 'just-debounce';
import rimg from 'rimg-shopify';
import AsyncView from 'shopify-asyncview';

let swatchSize = null;
let swatchGap = null;

export default class GridItemSwatches {
  constructor(options) {
    this.el = options.el;
    this.productQuickshop = options.productQuickshop;
    this.product = options.product;

    this.expanded = false;
    this.swatchImages = {};
    this.swatchVariantIds = {};
    this.selectedImage = null;
    this.state = {};
    this.events = [];

    this._injectImages = this._injectImages.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleMouseOver = this._handleMouseOver.bind(this);
    this._handleMouseLeave = this._handleMouseLeave.bind(this);
    this._toggleExpanded = this._toggleExpanded.bind(this);
    this._handleChange = this._handleChange.bind(this);
    this._handleResize = debounce(() => this.resize(), 50);

    this.data = JSON.parse(this.el.querySelector('[data-swatch-data]').innerHTML);

    this.swatchesContainer = this.el.querySelector('[data-swatches-container]');
    this.cardLinks = this.el.querySelectorAll('[data-product-page-link]');
    this.cardLinkHref = this.cardLinks[0].getAttribute('href');
    this.activeControlTime = 0;
    this.activeControl = this.swatchesContainer.querySelector('[checked]');
    this.isPreselected = !!this.activeControl;

    if (this.data.swatchesProductCardHover === 'always') {
      this.swatches = this.swatchesContainer.querySelectorAll('[data-swatch]');
      this.swatchCount = this.swatches.length;
      this.swatchCountWrapper = this.el.querySelector('[data-swatch-count-wrapper]');
      this.swatchCount = this.swatchCountWrapper.querySelector('[data-swatch-count]');
      this.resize();
      this.addEvent(window, 'resize', this._handleResize);
      this.addEvent(this.swatchCountWrapper, 'click', this._toggleExpanded);
    }

    this.originalImages = {
      primary: this.el.querySelector('.productitem--image-primary'),
      alternate: this.el.querySelector('.productitem--image-alternate'),
    };

    this.disableUnavailable();
    this.bindEvents();
  }

  disableUnavailable() {
    if (!this.product) {
      return;
    }

    const availableSwatches = {};

    this.product.variants.forEach(variant => {
      if (variant.available) {
        availableSwatches[variant[this.data.swatchOptionKey]] = true;
      }
    });

    const swatchInputs = this.swatchesContainer.querySelectorAll('input[name="swatch"]');

    swatchInputs.forEach(swatch => {
      if (!(swatch.value in availableSwatches)) {
        swatch.disabled = true;
      }
    });
  }

  addEvent(el, eventName, eventListener) {
    el.addEventListener(eventName, eventListener);
    this.events.push({ el, eventName, eventListener });
  }

  bindEvents() {
    this.addEvent(this.el, 'mouseover', this._injectImages);
    this.addEvent(this.swatchesContainer, 'mouseover', this._handleMouseOver);
    this.addEvent(this.swatchesContainer, 'mouseleave', this._handleMouseLeave);
    this.addEvent(this.swatchesContainer, 'click', this._handleClick);
    this.addEvent(this.swatchesContainer, 'change', this._handleChange);
  }

  resize() {
    if (this.data.swatchesProductCardHover === 'on-hover') return;
    if (this.expanded) this._toggleExpanded();

    const availableWidth = this.swatchesContainer.getBoundingClientRect().width
      - parseInt(window.getComputedStyle(this.swatchesContainer).paddingRight, 10);

    const newShowSwatchCount = Math.floor((availableWidth + this.getSwatchGap()) / (this.getSwatchSize() + this.getSwatchGap()));

    if (newShowSwatchCount === this.showSwatchCount) return;

    this.showSwatchCount = newShowSwatchCount;

    this.swatches.forEach((swatch, index) => {
      if (index < this.showSwatchCount) {
        swatch.classList.add('productitem--swatches-swatch-visible');
        swatch.classList.remove('productitem--swatches-swatch-hidden');
      } else {
        swatch.classList.remove('productitem--swatches-swatch-visible');
        swatch.classList.add('productitem--swatches-swatch-hidden');
      }
    });

    this.swatchCountWrapper.style.left = `${this.showSwatchCount * (this.getSwatchSize() + this.getSwatchGap())}px`;

    if (this.swatches.length > this.showSwatchCount) {
      this.swatchCountWrapper.style.display = 'flex';
      this.swatchCount.innerText = `+${this.swatches.length - this.showSwatchCount}`;
    } else {
      this.swatchCountWrapper.style.display = 'none';
    }
  }

  _injectImages() {
    this.el.removeEventListener('mouseover', this._injectImages);

    AsyncView.load(this.cardLinkHref, { view: '_swatch-data' }, { hash: this.data.hash })
      .then(({ data }) => {
        const tempContainer = document.createDocumentFragment();

        if (data.featuredImage) {
          const tempEl = document.createElement('div');
          tempEl.innerHTML = data.featuredImage;
          const img = tempEl.querySelector('img');
          tempContainer.appendChild(img);
          this.featuredImage = img;
        }

        data.swatches.forEach(({ swatchValue, imageString, variantId }) => {
          this.swatchVariantIds[swatchValue] = variantId;
          if (imageString) {
            const tempEl = document.createElement('div');
            tempEl.innerHTML = imageString;
            const img = tempEl.querySelector('img');
            tempContainer.appendChild(img);
            this.swatchImages[swatchValue] = img;
          }
        });
        const imagesContainer = this.el.querySelector('[data-product-item-image]');
        const salesBadge = imagesContainer.querySelector('[data-badge-sales]');

        imagesContainer.insertBefore(tempContainer, salesBadge);
        rimg.watch(imagesContainer);

        this._setState({
          swatchName: this.activeControl ? this.activeControl.value : null,
          primaryImage: this.originalImages.primary,
          hideAlternateImage: !!this.activeControl,
        });
      });
  }

  _setState({ swatchName, primaryImage, hideAlternateImage }) {
    const oldPrimaryImg = this.state.primaryImage;

    const href = swatchName ? `${this.cardLinkHref}?variant=${this.swatchVariantIds[swatchName]}` : this.cardLinkHref;
    this.cardLinks.forEach(link => link.setAttribute('href', href));

    if (this.productQuickshop) {
      this.productQuickshop.setInitialVariant(this.swatchVariantIds[swatchName] || this.product.variants[0].id);
    }

    if (oldPrimaryImg) {
      oldPrimaryImg.classList.remove('productitem--image-primary');
      oldPrimaryImg.style.visibility = '';
    }

    if (primaryImage) {
      primaryImage.classList.add('productitem--image-primary');
      primaryImage.style.visibility = hideAlternateImage ? 'visible' : '';
    }

    if (this.originalImages.alternate) {
      this.originalImages.alternate.style.visibility = hideAlternateImage ? 'hidden' : '';
    }

    this.state = { swatchName, primaryImage, hideAlternateImage };
  }

  _handleChange({ target }) {
    this.activeControlTime = Date.now();
    this.activeControl = target;
    const swatchName = target.value;
    this.selectedImage = this.swatchImages[swatchName] || this.originalImages.primary;
    this._setState({
      swatchName,
      primaryImage: this.selectedImage,
      hideAlternateImage: true,
    });
  }

  _handleClick({ target }) {
    if (target === this.activeControl && this.activeControlTime + 150 < Date.now()) {
      // Allow deselection, if swatch has been active for more than the threshold
      // because we can't guarantee the order of the click and change events
      target.checked = false;
      this.activeControl = null;
      this.selectedImage = null;

      // Swaps in featured image if card was rendered on server with preselected swatch variant image.
      if (this.isPreselected && this.featuredImage) {
        this.originalImages.primary = this.featuredImage;
        this.isPreselected = false;
      }

      this._setState({
        swatchName: null,
        primaryImage: this.state.primaryImage,
        hideAlternateImage: true,
      });
    }
  }

  _handleMouseOver(event) {
    if (event.target.hasAttribute('data-swatch-tooltip')) {
      this._setState({
        swatchName: this.state.swatchName,
        primaryImage: this.swatchImages[event.target.dataset.swatchTooltip] || this.originalImages.primary,
        hideAlternateImage: true,
      });
    }
  }

  _handleMouseLeave({ target }) {
    if (target.hasAttribute('data-swatches-container')) {
      this._setState({
        swatchName: this.state.swatchName,
        primaryImage: this.selectedImage || this.originalImages.primary,
        hideAlternateImage: !!this.selectedImage,
      });
    }
  }

  _toggleExpanded() {
    const swatchesEl = this.el.querySelector('[data-swatches]');

    if (this.expanded) {
      this.expanded = false;
      swatchesEl.classList.remove('productitem--swatches-expanded');
      this.swatchCount.innerText = `+${this.swatches.length - this.showSwatchCount}`;
    } else {
      this.expanded = true;
      swatchesEl.classList.add('productitem--swatches-expanded');
    }
  }

  // Getters are used to avoid having to get these properties for every card on the page
  getSwatchSize() {
    if (swatchSize === null) {
      swatchSize = this.swatches[0].getBoundingClientRect().width;
    }
    return swatchSize;
  }

  getSwatchGap() {
    if (swatchGap === null) {
      swatchGap = parseInt(window.getComputedStyle(this.swatchesContainer).getPropertyValue('grid-column-gap'), 10);
    }
    return swatchGap;
  }

  unload() {
    this.events.forEach(({ el, eventName, eventHandler }) => el.removeEventListener(eventName, eventHandler));
  }
}
